import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, DollarSign, Search, Filter, Edit2, Save, X,
  Shirt, Package, Calculator, Tag, ChevronDown, ChevronUp, User, Scissors,
  CheckCircle, AlertTriangle, XCircle, PackageCheck, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";
import { MX_INVENTORY, getInventoryStatus, getInventoryCounts } from "@/data/mxInventory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== MX SHIPPING COSTS ====================
// Non-CTD: $55 per suit, $35 vest
const MX_SHIPPING = {
  "2piece": 55, "3piece": 90, "jacket": 55, "pant": 55,
  "vest": 35, "overcoat": 55, "suit2pc": 55, "pants": 55, "shirt": 0,
};
// CTD: $55 per suit (2pc & 3pc), $55 individual pieces, $35 vest
const CTD_SHIPPING = {
  "2piece": 55, "3piece": 55, "jacket": 55, "pant": 55,
  "vest": 35, "overcoat": 55, "suit2pc": 55, "pants": 55, "shirt": 0,
};
// RC: $55 per piece, $35 vest
const RC_SHIPPING = {
  "2piece": 55, "3piece": 55, "jacket": 55, "pant": 55,
  "vest": 35, "overcoat": 55, "shirt": 0,
};

// ==================== CMT BASE PRICES (for computing premium tier prices) ====================
const CMT_BASE = {
  fused: { suit2pc: 115, jacket: 75, pants: 40, vest: 40 },
  halfCanvas: { suit2pc: 145, jacket: 105, pants: 40, vest: 40 },
  highEndHalfCanvas: { suit2pc: 176, jacket: 135, pants: 41, vest: 41 },
  fullCanvas: { suit2pc: 219, jacket: 175, pants: 44, vest: 44 },
  machineHalfCanvas: { suit2pc: 176, jacket: 135, pants: 41, vest: 41 },
  thirdHandmadeHalfCanvas: { suit2pc: 239, jacket: 182, pants: 57, vest: 57 },
  machineFullCanvas: { suit2pc: 219, jacket: 175, pants: 44, vest: 44 },
  thirdHandmadeFullCanvas: { suit2pc: 282, jacket: 222, pants: 60, vest: 60 },
  halfHandmadeFullCanvas: { suit2pc: 390, jacket: 260, pants: 130, vest: 130 },
  fullHandmadeFullCanvas: { suit2pc: 575, jacket: 430, pants: 145, vest: 130 },
};

// Compute landed price for any workmanship type from a category's fused price
const getLandedPrice = (categoryData, workmanship, garmentKey) => {
  // Direct lookup first
  if (categoryData[workmanship] && categoryData[workmanship][garmentKey] !== undefined && categoryData[workmanship][garmentKey] !== null) {
    return categoryData[workmanship][garmentKey];
  }
  // Compute from fused + CMT delta
  if (!categoryData.fused || !CMT_BASE[workmanship] || !CMT_BASE.fused) return null;
  const fusedPrice = categoryData.fused[garmentKey];
  if (fusedPrice === null || fusedPrice === undefined) return null;
  const delta = CMT_BASE[workmanship][garmentKey] - CMT_BASE.fused[garmentKey];
  if (typeof fusedPrice === 'string') {
    const parts = fusedPrice.split('/').map(Number);
    return parts.map(p => p + delta).join('/');
  }
  return fusedPrice + delta;
};

// All construction type options
const ALL_CONSTRUCTION_TYPES = [
  "Fused", "Half Canvas", "High-End Half Canvas", "Full Canvas",
  "Machine Half Canvas", "1/3 Handmade Half Canvas",
  "Machine Full Canvas", "1/3 Handmade Full Canvas",
  "1/2 Handmade Full Canvas", "Full Handmade Full Canvas"
];

// Map construction type label to workmanship key
const WORKMANSHIP_KEY_MAP = {
  "Fused": "fused",
  "Half Canvas": "halfCanvas",
  "High-End Half Canvas": "highEndHalfCanvas",
  "Full Canvas": "fullCanvas",
  "Machine Half Canvas": "machineHalfCanvas",
  "1/3 Handmade Half Canvas": "thirdHandmadeHalfCanvas",
  "Machine Full Canvas": "machineFullCanvas",
  "1/3 Handmade Full Canvas": "thirdHandmadeFullCanvas",
  "1/2 Handmade Full Canvas": "halfHandmadeFullCanvas",
  "Full Handmade Full Canvas": "fullHandmadeFullCanvas",
};

// ==================== RC FABRICS PRICING ====================
const DEFAULT_PRICING = {
  laborCosts: {
    "2piece": 180,
    "3piece": 230,
    "jacket": 130,
    "pant": 50,
    "vest": 50,
    "shirt": 30,
  },
  fabricUsage: {
    "2piece": 3.5,
    "3piece": 4.2,
    "jacket": 2.2,
    "pant": 1.3,
    "vest": 0.7,
    "shirt": 2.0,
  },
  markup: 3.0,
  constructionType: "Half Canvas",
  bigTallSurcharge: 0.15
};

const CONSTRUCTION_LABOR = {
  "Fused": { "2piece": 160, "3piece": 210, "jacket": 110, "pant": 50, "vest": 50, "shirt": 30 },
  "Half Canvas": { "2piece": 180, "3piece": 230, "jacket": 130, "pant": 50, "vest": 50, "shirt": 30 },
  "Full Canvas": { "2piece": 260, "3piece": 310, "jacket": 210, "pant": 50, "vest": 50, "shirt": 30 },
  "Unstructured Canvas": { "2piece": 180, "3piece": 230, "jacket": 135, "pant": 50, "vest": 50, "shirt": 30 },
  "Machine Half Canvas": { "2piece": 176, "3piece": 217, "jacket": 135, "pant": 41, "vest": 41, "shirt": 30 },
  "1/3 Handmade Half Canvas": { "2piece": 239, "3piece": 296, "jacket": 182, "pant": 57, "vest": 57, "shirt": 30 },
  "Machine Full Canvas": { "2piece": 219, "3piece": 263, "jacket": 175, "pant": 44, "vest": 44, "shirt": 30 },
  "1/3 Handmade Full Canvas": { "2piece": 282, "3piece": 342, "jacket": 222, "pant": 60, "vest": 60, "shirt": 30 },
  "1/2 Handmade Full Canvas": { "2piece": 390, "3piece": 520, "jacket": 260, "pant": 130, "vest": 130, "shirt": 30 },
  "Full Handmade Full Canvas": { "2piece": 575, "3piece": 705, "jacket": 430, "pant": 145, "vest": 130, "shirt": 30 }
};

const CTD_CONSTRUCTION_LABOR = {
  "Fused": { "2piece": 115, "3piece": 155, "jacket": 75, "pant": 40, "vest": 40, "shirt": 30 },
  "Half Canvas": { "2piece": 145, "3piece": 185, "jacket": 105, "pant": 40, "vest": 40, "shirt": 30 },
  "High-End Half Canvas": { "2piece": 176, "3piece": 217, "jacket": 135, "pant": 41, "vest": 41, "shirt": 30 },
  "Full Canvas": { "2piece": 219, "3piece": 263, "jacket": 175, "pant": 44, "vest": 44, "shirt": 30 },
  "Machine Half Canvas": { "2piece": 176, "3piece": 217, "jacket": 135, "pant": 41, "vest": 41, "shirt": 30 },
  "1/3 Handmade Half Canvas": { "2piece": 239, "3piece": 296, "jacket": 182, "pant": 57, "vest": 57, "shirt": 30 },
  "Machine Full Canvas": { "2piece": 219, "3piece": 263, "jacket": 175, "pant": 44, "vest": 44, "shirt": 30 },
  "1/3 Handmade Full Canvas": { "2piece": 282, "3piece": 342, "jacket": 222, "pant": 60, "vest": 60, "shirt": 30 },
  "1/2 Handmade Full Canvas": { "2piece": 390, "3piece": 520, "jacket": 260, "pant": 130, "vest": 130, "shirt": 30 },
  "Full Handmade Full Canvas": { "2piece": 575, "3piece": 705, "jacket": 430, "pant": 145, "vest": 130, "shirt": 30 }
};

const DEFAULT_CTD_PRICING = {
  laborCosts: {
    "2piece": 145,
    "3piece": 185,
    "jacket": 105,
    "pant": 40,
    "vest": 40,
    "shirt": 30,
  },
  fabricUsage: {
    "2piece": 3.5,
    "3piece": 4.2,
    "jacket": 2.2,
    "pant": 1.3,
    "vest": 0.7,
    "shirt": 2.0,
  },
  markup: 3.0,
  constructionType: "Half Canvas",
  bigTallSurcharge: 0.15
};

// ==================== MX WHOLESALE PRICING ====================
// Mapping of fabric code patterns to MX pricing categories
const getMXPricingCategory = (fabricCode) => {
  if (!fabricCode) return null;
  const code = fabricCode.toUpperCase();
  
  // 501 - M51xxx codes (Autumn/Winter jacketing)
  if (/^M51\d{3}/.test(code)) return "501";
  
  // 502 - N52xxx codes (Worsted Flannel)
  if (/^N52\d{3}/.test(code)) return "502 (N52101-N52130)";
  
  // 502 Premium - Q52xxx codes
  if (/^Q52\d{3}/.test(code)) return "502 (Q52131-Q52139)";
  
  // 503 - P53xxx codes (Flannel)
  if (/^P53\d{3}/.test(code)) return "503";
  
  // 504 - R54xxx codes (Overcoat)
  if (/^R54\d{3}/.test(code)) return "504 (R54301-R54313 & R54316)";
  
  // 504 Premium - S54xxx codes
  if (/^S54\d{3}/.test(code)) return "504 (S54314-S54315)";
  
  // 505-509 - P55xxx to P59xxx codes (Four Seasons)
  if (/^P5[5-9]\d{3}/.test(code)) return "505-509";
  
  // 510 - P510xxx codes (High Stretch)
  if (/^P510\d{3}/.test(code)) return "510";
  
  // 511 - P51xxx codes (Anti-Wrinkle) - 3 digit after P51
  if (/^P51[1-4]\d{2}$/.test(code)) return "511";
  
  // 601 - C60xxx codes (Overcoat) - must check BEFORE generic C60 pattern
  if (/^C60\d{3}/.test(code)) {
    const num = parseInt(code.substring(3));
    if (num >= 148 && num <= 149) return "601 (C60148-C60149)";
    if (num >= 143 && num <= 147) return "601 (C60143-C60147)";
    if (num >= 133 && num <= 135) return "601 (C60133-C60135)";
    return "601 (C60101-C60142)";
  }
  
  // 602 - S62xxx codes (Spring/Summer)
  if (/^S62\d{3}/.test(code)) {
    const num = parseInt(code.substring(3));
    if (num >= 61 && num <= 68) return "602 (S62061-S62068)";
    return "602 (S62011-S62060)";
  }
  
  // 513 - T70825[3]xx codes → 513/515 pricing category
  if (/^T708253\d{2}/.test(code)) return "513/515";
  
  // 514 - T70825[2]xx codes → 514/516 pricing category
  if (/^T708252\d{2}/.test(code)) return "514/516";
  
  // 515 - T70825[0]xx codes → 513/515 pricing category
  if (/^T708250\d{2}/.test(code)) return "513/515";
  
  // 516 - T70825[1]xx codes → 514/516 pricing category
  if (/^T708251\d{2}/.test(code)) return "514/516";
  
  // 517 - L517xx codes
  if (/^L517\d{2}/.test(code)) return "517";
  
  // 518 - L518xx codes
  if (/^L518\d{2}/.test(code)) return "518";
  
  // 519 - L519xx codes
  if (/^L519\d{2}/.test(code)) return "519";
  
  // ODM001 - NO.MX23xxx codes
  if (/^NO\.MX23/.test(code)) return "ODM001 Cotton";
  
  // ODM002 Jacquard - J39xxx/200 codes
  if (/J39\d{3}\/200/.test(code)) return "ODM002 Jacquard (/200)";
  
  // ODM002-2 Jacquard - J39xxx/220 codes
  if (/J39\d{3}\/220/.test(code)) return "ODM002 Jacquard (/220)";
  
  // ODM004 Classic - WL2335xxx
  if (/^WL2335\d{3}/.test(code)) return "ODM004 (Wool)";
  
  // M05 CEREMONY - A52024xxx (Fabric Code A)
  if (/^A52024\d{3}/.test(code)) return "Fabric Code A";
  
  // M05 CEREMONY - B52024xxx (Fabric Code B)
  if (/^B52024\d{3}/.test(code)) return "M05 & 402 (Code B)";
  
  // M05 CEREMONY - C52024xxx (Fabric Code C)
  if (/^C52024\d{3}/.test(code)) return "Fabric Code C";
  
  // KZT101, KZT201, KZT301, KZT501 - W8xxx codes (Muslin)
  if (/^W8[2469]\d{2}/.test(code)) return "ODM001 Cotton";
  
  // 401 Luxury Four Season - E10424xxx
  if (/^E10424\d{3}/.test(code)) return "401";
  
  // 403 Wool Jacquard - D50424xxx
  if (/^D50424\d{3}/.test(code)) return "403";
  
  return null;
};

// MX Fee Structure
const MX_FEES = {
  freight: 0.156,    // 15.6% Freight/Shipping
  duty: 0.08,        // 8.0% Duty
  bank: 0.045,       // 4.5% Bank charges
};

// Calculate final price with all fees
const calculateMXFinalPrice = (basePrice, includeShipping = false) => {
  if (!basePrice || basePrice === null) return null;
  const price = typeof basePrice === 'string' ? parseFloat(basePrice.split('/')[0]) : basePrice;
  if (isNaN(price)) return null;
  
  // Total markup: 15.6% + 8.0% + 4.5% = 28.1%
  const totalMarkup = 1 + MX_FEES.freight + MX_FEES.duty + MX_FEES.bank;
  const final = price * totalMarkup;
  return Math.round(final);
};

// Get fee breakdown for display
const getMXFeeBreakdown = (basePrice) => {
  if (!basePrice || basePrice === null) return null;
  const price = typeof basePrice === 'string' ? parseFloat(basePrice.split('/')[0]) : basePrice;
  if (isNaN(price)) return null;
  
  const freightAmount = price * MX_FEES.freight;
  const dutyAmount = price * MX_FEES.duty;
  const bankAmount = price * MX_FEES.bank;
  const final = price + freightAmount + dutyAmount + bankAmount;
  
  return {
    base: price,
    freight: Math.round(freightAmount),
    duty: Math.round(dutyAmount),
    bank: Math.round(bankAmount),
    final: Math.round(final),
  };
};

const MX_SUITS_DATA = [
  // CMT - Cut, Make, Trim Only (LABOR ONLY - No fabric included)
  { category: "CMT", description: "Labor Only (No Fabric)",
    isCMT: true,
    fused: { suit2pc: 115, jacket: 75, pants: 40, vest: 40, overcoat: null },
    budgetFused: { suit2pc: 68, jacket: 49, pants: 19, vest: 19, overcoat: null },
    halfCanvas: { suit2pc: 145, jacket: 105, pants: 40, vest: 40, overcoat: null },
    highEndHalfCanvas: { suit2pc: 176, jacket: 135, pants: 41, vest: 41, overcoat: null },
    fullCanvas: { suit2pc: 219, jacket: 175, pants: 44, vest: 44, overcoat: null },
    machineHalfCanvas: { suit2pc: 176, jacket: 135, pants: 41, vest: 41, overcoat: null },
    thirdHandmadeHalfCanvas: { suit2pc: 239, jacket: 182, pants: 57, vest: 57, overcoat: null },
    machineFullCanvas: { suit2pc: 219, jacket: 175, pants: 44, vest: 44, overcoat: null },
    thirdHandmadeFullCanvas: { suit2pc: 282, jacket: 222, pants: 60, vest: 60, overcoat: null },
    halfHandmadeFullCanvas: { suit2pc: 390, jacket: 260, pants: 130, vest: 130, overcoat: null },
    fullHandmadeFullCanvas: { suit2pc: 575, jacket: 430, pants: 145, vest: 130, overcoat: null },
    muslinFitting: { suit2pc: 60, jacket: 45, pants: 15, vest: 10, overcoat: 50 },
    basteFitting: { suit2pc: 35, jacket: 25, pants: 10, vest: 10, overcoat: 25 },
  },
  // ============ ALL-INCLUSIVE PRICES (Fabric + Production + Finished Garment) ============
  // 513/515 - New Book
  { category: "513/515", description: "New Book (All-Inclusive)",
    fused: { suit2pc: 139, jacket: 89, pants: 50, vest: "46/48", overcoat: null },
    budgetFused: { suit2pc: 92, jacket: 63, pants: 29, vest: "25/27", overcoat: null },
    halfCanvas: { suit2pc: 169, jacket: 119, pants: 50, vest: "46/48", overcoat: null },
    highEndHalfCanvas: { suit2pc: 200, jacket: 149, pants: 51, vest: "47/49", overcoat: null },
    fullCanvas: { suit2pc: 243, jacket: 189, pants: 54, vest: "50/52", overcoat: null },
  },
  // 514/516 - New Book
  { category: "514/516", description: "New Book (All-Inclusive)",
    fused: { suit2pc: 142, jacket: 91, pants: 51, vest: "46/50", overcoat: null },
    budgetFused: { suit2pc: 95, jacket: 65, pants: 30, vest: "25/29", overcoat: null },
    halfCanvas: { suit2pc: 172, jacket: 121, pants: 51, vest: "46/50", overcoat: null },
    highEndHalfCanvas: { suit2pc: 203, jacket: 151, pants: 52, vest: "47/51", overcoat: null },
    fullCanvas: { suit2pc: 246, jacket: 191, pants: 55, vest: "50/54", overcoat: null },
  },
  // 517
  { category: "517", description: "Premium Wool (All-Inclusive)",
    fused: { suit2pc: 180, jacket: 113, pants: 67, vest: "55/63", overcoat: null },
    budgetFused: { suit2pc: 133, jacket: 87, pants: 46, vest: "34/42", overcoat: null },
    halfCanvas: { suit2pc: 210, jacket: 143, pants: 67, vest: "55/63", overcoat: null },
    highEndHalfCanvas: { suit2pc: 241, jacket: 173, pants: 68, vest: "56/64", overcoat: null },
    fullCanvas: { suit2pc: 284, jacket: 213, pants: 71, vest: "59/67", overcoat: null },
  },
  // 518
  { category: "518", description: "Luxury Wool (All-Inclusive)",
    fused: { suit2pc: 166, jacket: 105, pants: 61, vest: "52/58", overcoat: null },
    budgetFused: { suit2pc: 119, jacket: 79, pants: 40, vest: "31/37", overcoat: null },
    halfCanvas: { suit2pc: 196, jacket: 135, pants: 61, vest: "52/58", overcoat: null },
    highEndHalfCanvas: { suit2pc: 227, jacket: 165, pants: 62, vest: "53/59", overcoat: null },
    fullCanvas: { suit2pc: 270, jacket: 205, pants: 65, vest: "56/62", overcoat: null },
  },
  // 519
  { category: "519", description: "Designer Wool (All-Inclusive)",
    fused: { suit2pc: 156, jacket: 99, pants: 57, vest: "50/54", overcoat: null },
    budgetFused: { suit2pc: 109, jacket: 73, pants: 36, vest: "29/33", overcoat: null },
    halfCanvas: { suit2pc: 186, jacket: 129, pants: 57, vest: "50/54", overcoat: null },
    highEndHalfCanvas: { suit2pc: 217, jacket: 159, pants: 58, vest: "51/55", overcoat: null },
    fullCanvas: { suit2pc: 260, jacket: 199, pants: 61, vest: "54/58", overcoat: null },
  },
  // 601 (C60101-C60132, C60136-C60142)
  { category: "601 (C60101-C60142)", description: "Overcoat Standard (All-Inclusive)",
    fused: { suit2pc: 176, jacket: 111, pants: 65, vest: "54/62", overcoat: null },
    budgetFused: { suit2pc: 129, jacket: 85, pants: 44, vest: "33/41", overcoat: null },
    halfCanvas: { suit2pc: 206, jacket: 141, pants: 65, vest: "54/62", overcoat: null },
    highEndHalfCanvas: { suit2pc: 237, jacket: 171, pants: 66, vest: "55/63", overcoat: null },
    fullCanvas: { suit2pc: 280, jacket: 211, pants: 69, vest: "58/66", overcoat: null },
  },
  // 601 (C60133-C60135)
  { category: "601 (C60133-C60135)", description: "Overcoat Premium (All-Inclusive)",
    fused: { suit2pc: 217, jacket: 135, pants: 82, vest: "64/76", overcoat: null },
    budgetFused: { suit2pc: 170, jacket: 109, pants: 61, vest: "43/55", overcoat: null },
    halfCanvas: { suit2pc: 247, jacket: 165, pants: 82, vest: "64/76", overcoat: null },
    highEndHalfCanvas: { suit2pc: 278, jacket: 195, pants: 83, vest: "65/77", overcoat: null },
    fullCanvas: { suit2pc: 321, jacket: 235, pants: 86, vest: "68/80", overcoat: null },
  },
  // 601 (C60143-C60147)
  { category: "601 (C60143-C60147)", description: "Overcoat Luxury (All-Inclusive)",
    fused: { suit2pc: 292, jacket: 179, pants: 113, vest: "82/102", overcoat: null },
    budgetFused: { suit2pc: 245, jacket: 153, pants: 92, vest: "61/81", overcoat: null },
    halfCanvas: { suit2pc: 322, jacket: 209, pants: 113, vest: "82/102", overcoat: null },
    highEndHalfCanvas: { suit2pc: 353, jacket: 239, pants: 114, vest: "83/103", overcoat: null },
    fullCanvas: { suit2pc: 396, jacket: 279, pants: 117, vest: "86/106", overcoat: null },
  },
  // 601 (C60148-C60149)
  { category: "601 (C60148-C60149)", description: "Overcoat Ultra (All-Inclusive)",
    fused: { suit2pc: 319, jacket: 195, pants: 124, vest: "88/112", overcoat: null },
    budgetFused: { suit2pc: 272, jacket: 169, pants: 103, vest: "67/91", overcoat: null },
    halfCanvas: { suit2pc: 349, jacket: 225, pants: 124, vest: "88/112", overcoat: null },
    highEndHalfCanvas: { suit2pc: 380, jacket: 255, pants: 125, vest: "89/113", overcoat: null },
    fullCanvas: { suit2pc: 423, jacket: 295, pants: 128, vest: "92/116", overcoat: null },
  },
  // 602 (S62011-S62060)
  { category: "602 (S62011-S62060)", description: "Spring/Summer Standard (All-Inclusive)",
    fused: { suit2pc: 234, jacket: 145, pants: 89, vest: "68/82", overcoat: null },
    budgetFused: { suit2pc: 187, jacket: 119, pants: 68, vest: "47/61", overcoat: null },
    halfCanvas: { suit2pc: 264, jacket: 175, pants: 89, vest: "68/82", overcoat: null },
    highEndHalfCanvas: { suit2pc: 295, jacket: 205, pants: 90, vest: "69/83", overcoat: null },
    fullCanvas: { suit2pc: 338, jacket: 245, pants: 93, vest: "72/86", overcoat: null },
  },
  // 602 (S62061-S62068)
  { category: "602 (S62061-S62068)", description: "Spring/Summer Premium (All-Inclusive)",
    fused: { suit2pc: 210, jacket: 131, pants: 79, vest: "62/74", overcoat: null },
    budgetFused: { suit2pc: 163, jacket: 105, pants: 58, vest: "41/53", overcoat: null },
    halfCanvas: { suit2pc: 240, jacket: 161, pants: 79, vest: "62/74", overcoat: null },
    highEndHalfCanvas: { suit2pc: 271, jacket: 191, pants: 80, vest: "63/75", overcoat: null },
    fullCanvas: { suit2pc: 314, jacket: 231, pants: 83, vest: "66/78", overcoat: null },
  },
  // 101_CLASSIC BUSINESS
  { category: "101_CLASSIC BUSINESS", description: "Classic Business (All-Inclusive)",
    fused: { suit2pc: 203, jacket: 127, pants: 76, vest: "61/71", overcoat: null },
    budgetFused: { suit2pc: 156, jacket: 101, pants: 55, vest: "40/50", overcoat: null },
    halfCanvas: { suit2pc: 233, jacket: 157, pants: 76, vest: "61/71", overcoat: null },
    highEndHalfCanvas: { suit2pc: 264, jacket: 187, pants: 77, vest: "62/72", overcoat: null },
    fullCanvas: { suit2pc: 307, jacket: 227, pants: 80, vest: "65/75", overcoat: null },
  },
  // 201_FIGHTER
  { category: "201_FIGHTER", description: "Fighter Collection (All-Inclusive)",
    fused: { suit2pc: 217, jacket: 135, pants: 82, vest: "64/76", overcoat: null },
    budgetFused: { suit2pc: 170, jacket: 109, pants: 61, vest: "43/55", overcoat: null },
    halfCanvas: { suit2pc: 247, jacket: 165, pants: 82, vest: "64/76", overcoat: null },
    highEndHalfCanvas: { suit2pc: 278, jacket: 195, pants: 83, vest: "65/77", overcoat: null },
    fullCanvas: { suit2pc: 321, jacket: 235, pants: 86, vest: "68/80", overcoat: null },
  },
  // 301_FOCUS
  { category: "301_FOCUS", description: "Focus Collection (All-Inclusive)",
    fused: { suit2pc: 217, jacket: 135, pants: 82, vest: "64/76", overcoat: null },
    budgetFused: { suit2pc: 170, jacket: 109, pants: 61, vest: "43/55", overcoat: null },
    halfCanvas: { suit2pc: 247, jacket: 165, pants: 82, vest: "64/76", overcoat: null },
    highEndHalfCanvas: { suit2pc: 278, jacket: 195, pants: 83, vest: "65/77", overcoat: null },
    fullCanvas: { suit2pc: 321, jacket: 235, pants: 86, vest: "68/80", overcoat: null },
  },
  // 501_FLANNEL & CAVALRY
  { category: "501_FLANNEL & CAVALRY", description: "Flannel & Cavalry (All-Inclusive)",
    fused: { suit2pc: 217, jacket: 135, pants: 82, vest: "64/76", overcoat: null },
    budgetFused: { suit2pc: 170, jacket: 109, pants: 61, vest: "43/55", overcoat: null },
    halfCanvas: { suit2pc: 247, jacket: 165, pants: 82, vest: "64/76", overcoat: null },
    highEndHalfCanvas: { suit2pc: 278, jacket: 195, pants: 83, vest: "65/77", overcoat: null },
    fullCanvas: { suit2pc: 321, jacket: 235, pants: 86, vest: "68/80", overcoat: null },
  },
  // Legacy categories (All-Inclusive - Fabric + Production)
  { category: "501", description: "Premium Collection (All-Inclusive)",
    fused: { suit2pc: 209, jacket: 130, pants: null, vest: null, overcoat: 174 },
    halfCanvas: { suit2pc: 239, jacket: 160, pants: 84, vest: "62/73", overcoat: 204 },
    fullCanvas: { suit2pc: 313, jacket: 230, pants: 88, vest: "66/77", overcoat: 288 },
  },
  { category: "502 (N52101-N52130)", description: "Standard Range (All-Inclusive)",
    fused: { suit2pc: 198, jacket: 124, pants: null, vest: null, overcoat: 166 },
    halfCanvas: { suit2pc: 228, jacket: 154, pants: 80, vest: "60/69", overcoat: 196 },
    fullCanvas: { suit2pc: 302, jacket: 224, pants: 84, vest: "64/73", overcoat: 280 },
  },
  { category: "502 (Q52131-Q52139)", description: "Premium Range (All-Inclusive)",
    fused: { suit2pc: 237, jacket: 147, pants: null, vest: null, overcoat: 195 },
    halfCanvas: { suit2pc: 267, jacket: 177, pants: 98, vest: "69/83", overcoat: 225 },
    fullCanvas: { suit2pc: 341, jacket: 247, pants: 102, vest: "73/87", overcoat: 309 },
  },
  { category: "503", description: "Value Collection (All-Inclusive)",
    fused: { suit2pc: 163, jacket: 103, pants: null, vest: null, overcoat: 140 },
    halfCanvas: { suit2pc: 193, jacket: 133, pants: 63, vest: "51/57", overcoat: 170 },
    fullCanvas: { suit2pc: 267, jacket: 203, pants: 67, vest: "55/61", overcoat: 254 },
  },
  { category: "504 (R54301-R54313 & R54316)", description: "Designer Range (All-Inclusive)",
    fused: { suit2pc: 224, jacket: 139, pants: null, vest: null, overcoat: 185 },
    halfCanvas: { suit2pc: 254, jacket: 169, pants: 92, vest: "66/78", overcoat: 215 },
    fullCanvas: { suit2pc: 328, jacket: 239, pants: 96, vest: "70/82", overcoat: 299 },
  },
  { category: "504 (S54314-S54315)", description: "Luxury Designer (All-Inclusive)",
    fused: { suit2pc: 263, jacket: 162, pants: null, vest: null, overcoat: 214 },
    halfCanvas: { suit2pc: 293, jacket: 192, pants: 110, vest: "75/92", overcoat: 244 },
    fullCanvas: { suit2pc: 367, jacket: 262, pants: 114, vest: "79/96", overcoat: 328 },
  },
  { category: "505-509", description: "Four Seasons (All-Inclusive)",
    fused: { suit2pc: 168, jacket: 106, pants: null, vest: null, overcoat: 144 },
    halfCanvas: { suit2pc: 198, jacket: 136, pants: 65, vest: "52/59", overcoat: 174 },
    fullCanvas: { suit2pc: 272, jacket: 206, pants: 69, vest: "56/63", overcoat: 258 },
  },
  { category: "510", description: "High Stretch (All-Inclusive)",
    fused: { suit2pc: 169, jacket: 107, pants: null, vest: null, overcoat: 145 },
    halfCanvas: { suit2pc: 199, jacket: 137, pants: 66, vest: "53/59", overcoat: 175 },
    fullCanvas: { suit2pc: 273, jacket: 207, pants: 70, vest: "57/63", overcoat: 259 },
  },
  { category: "511", description: "Anti-Wrinkle (All-Inclusive)",
    fused: { suit2pc: 168, jacket: 106, pants: null, vest: null, overcoat: 144 },
    halfCanvas: { suit2pc: 198, jacket: 136, pants: 65, vest: "52/59", overcoat: 174 },
    fullCanvas: { suit2pc: 272, jacket: 206, pants: 69, vest: "56/63", overcoat: 258 },
  },
  { category: "401", description: "Luxury Four Season (All-Inclusive)",
    fused: { suit2pc: 197, jacket: 123, pants: null, vest: null, overcoat: 165 },
    halfCanvas: { suit2pc: 227, jacket: 153, pants: 79, vest: "59/69", overcoat: 195 },
    fullCanvas: { suit2pc: 301, jacket: 223, pants: 83, vest: "63/73", overcoat: 279 },
  },
  { category: "403", description: "Wool Jacquard (All-Inclusive)",
    fused: { suit2pc: 203, jacket: 127, pants: null, vest: null, overcoat: 170 },
    halfCanvas: { suit2pc: 233, jacket: 157, pants: 82, vest: "61/71", overcoat: 200 },
    fullCanvas: { suit2pc: 307, jacket: 227, pants: 86, vest: "65/75", overcoat: 284 },
  },
  { category: "ODM004 (Wool)", description: "Wool Collection (All-Inclusive)",
    fused: { suit2pc: 156, jacket: 99, pants: null, vest: null, overcoat: 135 },
    halfCanvas: { suit2pc: 186, jacket: 129, pants: 60, vest: "50/54", overcoat: 165 },
    fullCanvas: { suit2pc: 260, jacket: 199, pants: 64, vest: "54/58", overcoat: 249 },
  },
  { category: "ODM001 Cotton", description: "Cotton Collection (All-Inclusive)",
    fused: { suit2pc: 169, jacket: 107, pants: null, vest: null, overcoat: 145 },
    halfCanvas: { suit2pc: 199, jacket: 137, pants: 66, vest: "53/59", overcoat: 175 },
    fullCanvas: { suit2pc: 273, jacket: 207, pants: 70, vest: "57/63", overcoat: 259 },
  },
  { category: "ODM002 Jacquard (/220)", description: "Jacquard Standard (All-Inclusive)",
    fused: { suit2pc: 169, jacket: 107, pants: null, vest: null, overcoat: 145 },
    halfCanvas: { suit2pc: 199, jacket: 137, pants: 66, vest: "53/59", overcoat: 175 },
    fullCanvas: { suit2pc: 273, jacket: 207, pants: 70, vest: "57/63", overcoat: 259 },
  },
  { category: "ODM002 Jacquard (/200)", description: "Jacquard Premium (All-Inclusive)",
    fused: { suit2pc: 190, jacket: 119, pants: null, vest: null, overcoat: 160 },
    halfCanvas: { suit2pc: 220, jacket: 149, pants: 76, vest: "58/66", overcoat: 190 },
    fullCanvas: { suit2pc: 294, jacket: 219, pants: 80, vest: "62/70", overcoat: 274 },
  },
  { category: "Fabric Code A", description: "Premium Fabric (All-Inclusive)",
    fused: { suit2pc: 217, jacket: 135, pants: null, vest: null, overcoat: 180 },
    halfCanvas: { suit2pc: 247, jacket: 165, pants: 88, vest: "64/76", overcoat: 210 },
    fullCanvas: { suit2pc: 321, jacket: 235, pants: 92, vest: "69/81", overcoat: 294 },
  },
  { category: "M05 & 402 (Code B)", description: "Standard Fabric (All-Inclusive)",
    fused: { suit2pc: 200, jacket: 125, pants: null, vest: null, overcoat: 168 },
    halfCanvas: { suit2pc: 230, jacket: 155, pants: 80, vest: "60/70", overcoat: 198 },
    fullCanvas: { suit2pc: 304, jacket: 225, pants: 84, vest: "65/75", overcoat: 282 },
  },
  { category: "Fabric Code C", description: "Value Fabric (All-Inclusive)",
    fused: { suit2pc: 190, jacket: 119, pants: null, vest: null, overcoat: 160 },
    halfCanvas: { suit2pc: 220, jacket: 149, pants: 76, vest: "58/66", overcoat: 190 },
    fullCanvas: { suit2pc: 294, jacket: 219, pants: 80, vest: "63/71", overcoat: 274 },
  },
];

const MX_SHIRTS_DATA = [
  { category: "406 / 407 / 408", fabric: "H Fabric", price: 35 },
  { category: "409", fabric: "G Fabric", price: 42 },
  { category: "410", fabric: "J Fabric", price: 51 },
  { category: "410", fabric: "K Fabric", price: 59 },
  { category: "415", fabric: "S Fabric", price: 77 },
  { category: "415", fabric: "J Fabric", price: 41 },
];

const MX_BUTTONS_DATA = [
  { item: "Shell button BS25102-BS25124", jacket: 9, pants: 3, vest: 3, shirt: null },
  { item: "Shell button GS25601-GS25622", jacket: null, pants: null, vest: null, shirt: 6 },
  { item: "Shell button GCE25623-GCE25626", jacket: null, pants: null, vest: null, shirt: 9 },
  { item: "Shell button GC25627-GC25631 & GPM25632-GPM25636", jacket: null, pants: null, vest: null, shirt: 4.5 },
];

const AdminPriceListPage = ({ hideHeader = false } = {}) => {
  const navigate = useNavigate();
  const { FABRIC_DATA, MX_FABRIC_DATA } = useFabricCatalog();
  
  // Main tab state
  const [mainTab, setMainTab] = useState("rc-fabrics"); // "rc-fabrics" or "mx-wholesale"
  
  // RC Fabrics state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBook, setSelectedBook] = useState("all");
  const [sortBy, setSortBy] = useState("price-asc");
  const [expandedFabric, setExpandedFabric] = useState(null);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [ctdPricing, setCtdPricing] = useState(DEFAULT_CTD_PRICING);
  const [editingPricing, setEditingPricing] = useState(false);
  const [tempPricing, setTempPricing] = useState(DEFAULT_PRICING);
  const [isBigTall, setIsBigTall] = useState(false);
  
  // MX Wholesale state
  const [mxTab, setMxTab] = useState("fabrics");
  const [mxSearchTerm, setMxSearchTerm] = useState("");
  const [workmanshipFilter, setWorkmanshipFilter] = useState("all");
  const [mxSearchQuery, setMxSearchQuery] = useState("");
  const [mxSelectedBook, setMxSelectedBook] = useState("all");
  const [mxSortBy, setMxSortBy] = useState("price-asc");
  const [mxExpandedFabric, setMxExpandedFabric] = useState(null);
  const [mxConstructionType, setMxConstructionType] = useState("Half Canvas");
  const [stockFilter, setStockFilter] = useState("all"); // "all", "in_stock", "need_purchase"
  const [mxMarkup, setMxMarkup] = useState(1.0); // MX markup multiplier (1.0 = wholesale/no markup)
  const [showMxCmtRef, setShowMxCmtRef] = useState(false);
  const [showCtdProcessRef, setShowCtdProcessRef] = useState(false);
  const [showCtdProductionTime, setShowCtdProductionTime] = useState(false);
  
  // Calculate inventory statistics
  const inventoryStats = useMemo(() => getInventoryCounts(), []);

  // Get unique books
  const books = useMemo(() => {
    const bookSet = new Set((FABRIC_DATA.suits || []).map(f => f.book));
    return ["all", ...Array.from(bookSet).sort()];
  }, [FABRIC_DATA]);

  // Filter and sort fabrics
  const filteredFabrics = useMemo(() => {
    let fabrics = [...FABRIC_DATA.suits];
    if (selectedBook !== "all") fabrics = fabrics.filter(f => f.book === selectedBook);
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      fabrics = fabrics.filter(f => 
        f.rc_code.toLowerCase().includes(query) ||
        f.book.toLowerCase().includes(query) ||
        f.color.toLowerCase().includes(query) ||
        f.pattern.toLowerCase().includes(query) ||
        f.composition.toLowerCase().includes(query) ||
        (f.yarn && f.yarn.toLowerCase().includes(query))
      );
    }
    switch (sortBy) {
      case "price-asc": fabrics.sort((a, b) => a.price - b.price); break;
      case "price-desc": fabrics.sort((a, b) => b.price - a.price); break;
      case "code": fabrics.sort((a, b) => a.rc_code.localeCompare(b.rc_code)); break;
      case "color": fabrics.sort((a, b) => a.color.localeCompare(b.color)); break;
      default: break;
    }
    return fabrics;
  }, [searchQuery, selectedBook, sortBy, FABRIC_DATA]);

  // Calculate garment price (RC with shipping)
  const calculatePrice = (fabricPrice, garmentType, applyBigTall = isBigTall) => {
    const fabricCost = fabricPrice * pricing.fabricUsage[garmentType];
    const laborCost = pricing.laborCosts[garmentType];
    const shipping = RC_SHIPPING[garmentType] || 55;
    const totalCost = fabricCost + laborCost + shipping;
    let finalPrice = totalCost * pricing.markup;
    if (applyBigTall) finalPrice = finalPrice * (1 + pricing.bigTallSurcharge);
    return Math.round(finalPrice);
  };

  const calculateCtdPrice = (fabricPrice, garmentType, applyBigTall = isBigTall) => {
    const fabricCost = fabricPrice * ctdPricing.fabricUsage[garmentType];
    const laborCost = ctdPricing.laborCosts[garmentType];
    const shipping = CTD_SHIPPING[garmentType] || 55;
    const totalCost = fabricCost + laborCost + shipping;
    let finalPrice = totalCost * ctdPricing.markup;
    if (applyBigTall) finalPrice = finalPrice * (1 + ctdPricing.bigTallSurcharge);
    return Math.round(finalPrice);
  };

  // Save pricing changes
  const savePricing = () => {
    setPricing(tempPricing);
    setEditingPricing(false);
    toast.success("Pricing updated!");
  };

  // Get fabric tier
  const getFabricTier = (price) => {
    if (price <= 15) return { name: "Economy", color: "text-green-400", bg: "bg-green-500/20" };
    if (price <= 25) return { name: "Standard", color: "text-blue-400", bg: "bg-blue-500/20" };
    if (price <= 40) return { name: "Premium", color: "text-purple-400", bg: "bg-purple-500/20" };
    return { name: "Luxury", color: "text-amber-400", bg: "bg-amber-500/20" };
  };

  // MX Wholesale Labor Costs (wholesale pricing)
  const MX_CONSTRUCTION_LABOR = {
    "Fused": { "2piece": 145, "3piece": 185, "jacket": 95, "pant": 40, "vest": 40 },
    "Half Canvas": { "2piece": 195, "3piece": 250, "jacket": 135, "pant": 45, "vest": 45 },
    "High-End Half Canvas": { "2piece": 230, "3piece": 290, "jacket": 160, "pant": 47, "vest": 47 },
    "Full Canvas": { "2piece": 270, "3piece": 340, "jacket": 190, "pant": 50, "vest": 50 },
    "Machine Half Canvas": { "2piece": 230, "3piece": 290, "jacket": 160, "pant": 47, "vest": 47 },
    "1/3 Handmade Half Canvas": { "2piece": 296, "3piece": 370, "jacket": 212, "pant": 62, "vest": 62 },
    "Machine Full Canvas": { "2piece": 270, "3piece": 340, "jacket": 190, "pant": 50, "vest": 50 },
    "1/3 Handmade Full Canvas": { "2piece": 340, "3piece": 420, "jacket": 247, "pant": 65, "vest": 65 },
    "1/2 Handmade Full Canvas": { "2piece": 450, "3piece": 580, "jacket": 290, "pant": 135, "vest": 135 },
    "Full Handmade Full Canvas": { "2piece": 635, "3piece": 775, "jacket": 460, "pant": 150, "vest": 135 }
  };

  // MX Fabric Usage (same as retail)
  const MX_FABRIC_USAGE = {
    "2piece": 3.5,
    "3piece": 4.2,
    "jacket": 2.2,
    "pant": 1.3,
    "vest": 0.7,
  };

  // Filter and sort MX fabrics
  const filteredMXFabrics = useMemo(() => {
    let fabrics = [...MX_FABRIC_DATA];
    if (mxSelectedBook !== "all") fabrics = fabrics.filter(f => f.book === mxSelectedBook);
    if (mxSearchQuery) {
      const query = mxSearchQuery.toLowerCase();
      fabrics = fabrics.filter(f => 
        f.rc_code.toLowerCase().includes(query) ||
        f.color.toLowerCase().includes(query) ||
        f.pattern.toLowerCase().includes(query) ||
        f.composition.toLowerCase().includes(query)
      );
    }
    // Apply stock filter
    if (stockFilter !== "all") {
      fabrics = fabrics.filter(f => {
        const status = getInventoryStatus(f.rc_code);
        if (stockFilter === "in_stock") return status === "in_stock";
        if (stockFilter === "need_purchase") return status === "need_purchase" || status === "failed";
        if (stockFilter === "tracked") return status !== null;
        return true;
      });
    }
    switch (mxSortBy) {
      case "price-asc": fabrics.sort((a, b) => a.price - b.price); break;
      case "price-desc": fabrics.sort((a, b) => b.price - a.price); break;
      case "code": fabrics.sort((a, b) => a.rc_code.localeCompare(b.rc_code)); break;
      case "color": fabrics.sort((a, b) => a.color.localeCompare(b.color)); break;
      case "stock": fabrics.sort((a, b) => {
        const statusA = getInventoryStatus(a.rc_code);
        const statusB = getInventoryStatus(b.rc_code);
        const order = { "in_stock": 0, "need_purchase": 1, "failed": 2, null: 3 };
        return (order[statusA] ?? 3) - (order[statusB] ?? 3);
      }); break;
      default: break;
    }
    return fabrics;
  }, [mxSearchQuery, mxSelectedBook, mxSortBy, stockFilter, MX_FABRIC_DATA]);
  
  // Get unique MX books for filter dropdown
  const mxBooks = useMemo(() => {
    const uniqueBooks = [...new Set((MX_FABRIC_DATA || []).map(f => f.book))];
    return ["all", ...uniqueBooks.sort()];
  }, [MX_FABRIC_DATA]);

  // Check if current MX construction type is CTD
  const isCTD = mxConstructionType.startsWith("CTD ");

  // Get correct shipping table based on construction type
  const getShipping = (garmentKey) => {
    const table = isCTD ? CTD_SHIPPING : MX_SHIPPING;
    return table[garmentKey] || 55;
  };

  // Calculate MX wholesale price with optional markup + shipping
  const calculateMXPrice = (fabricPrice, garmentType) => {
    const fabricCost = fabricPrice * MX_FABRIC_USAGE[garmentType];
    const laborCost = MX_CONSTRUCTION_LABOR[mxConstructionType][garmentType];
    const shipping = getShipping(garmentType);
    const totalCost = fabricCost + laborCost + shipping;
    return Math.round(totalCost * mxMarkup);
  };

  // MX filter
  const filteredMXSuits = MX_SUITS_DATA.filter(item => 
    item.category.toLowerCase().includes(mxSearchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(mxSearchTerm.toLowerCase())
  );

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "–";
    if (typeof price === "string") return `$${price}`;
    return `$${price}`;
  };

  return (
    <div className={hideHeader ? "text-white" : "min-h-screen bg-[#0a1628] text-white"}>
      {!hideHeader && (
      /* Header */
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin")} className="text-slate-400 hover:text-white">
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <DollarSign size={24} className="text-emerald-400" />
                Price List
              </h1>
              <p className="text-sm text-slate-400">
                {mainTab === "rc-fabrics" ? `${pricing.constructionType} • ${pricing.markup}× Markup` : 
                 mainTab === "mx-retail" ? `MX ${mxConstructionType} • ${mxMarkup}× Retail Markup` :
                 `MX ${mxConstructionType} • ${mxMarkup === 1.0 ? "Wholesale" : mxMarkup + "× Markup"}`}
              </p>
            </div>
          </div>
          {(mainTab === "rc-fabrics" || mainTab === "mx-retail") && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsBigTall(!isBigTall)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                  isBigTall ? "bg-amber-500 text-black" : "bg-[#0a1628] border border-white/10 text-slate-400 hover:border-amber-500/50"
                }`}
              >
                <User size={16} />
                Big & Tall {isBigTall && "+15%"}
              </button>
              <Button variant="outline" size="sm" onClick={() => { setTempPricing(pricing); setEditingPricing(true); }} className="border-white/20">
                <Edit2 size={16} className="mr-2" /> Edit Pricing
              </Button>
            </div>
          )}
        </div>
      </header>
      )}

      {/* Main Tabs */}
      <div className="bg-[#111c2e] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => setMainTab("rc-fabrics")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                mainTab === "rc-fabrics" ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              RC Fabrics (Retail)
            </button>
            <button
              onClick={() => { setMainTab("mx-retail"); if (mxMarkup < 2) setMxMarkup(3.0); }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                mainTab === "mx-retail" ? "border-purple-500 text-purple-400" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              MX Fabrics (Retail)
            </button>
          </div>
        </div>
      </div>

      {/* RC Fabrics Content */}
      {mainTab === "rc-fabrics" && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Quick Settings Bar */}
          <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-400">Construction:</span>
                <div className="flex gap-1 flex-wrap">
                  {["Fused", "Half Canvas", "Full Canvas", "Unstructured Canvas"].map(type => (
                    <button
                      key={type}
                      onClick={() => setPricing(prev => ({ ...prev, constructionType: type, laborCosts: CONSTRUCTION_LABOR[type] }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        pricing.constructionType === type ? "bg-blue-500 text-white" : "bg-[#0a1628] text-slate-400 hover:text-white"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                  <span className="text-xs text-teal-400 font-semibold self-center mx-1">CTD:</span>
                  {ALL_CONSTRUCTION_TYPES.map(type => (
                    <button
                      key={`ctd-${type}`}
                      onClick={() => setPricing(prev => ({ ...prev, constructionType: `CTD ${type}`, laborCosts: CTD_CONSTRUCTION_LABOR[type] }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        pricing.constructionType === `CTD ${type}` ? "bg-teal-500 text-white" : "bg-[#0a1628] text-slate-400 hover:text-white"
                      }`}
                    >
                      CTD {type}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Markup:</span>
                <div className="flex gap-1">
                  {[2.0, 2.5, 3.0, 3.5, 4.0].map(mult => (
                    <button
                      key={mult}
                      onClick={() => setPricing(prev => ({ ...prev, markup: mult }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        pricing.markup === mult ? "bg-emerald-500 text-white" : "bg-[#0a1628] text-slate-400 hover:text-white"
                      }`}
                    >
                      {mult}×
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Fee Structure Info - Same as MX */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/30 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-amber-400 mb-1">
                  RC Fee Structure (+$55 Ship, $35 Vest + 4.5% Bank)
                </h3>
                <p className="text-sm text-slate-400">Prices shown: <span className="text-slate-300">Base Cost</span> → <span className="text-emerald-400">Final Retail (incl. shipping + markup)</span></p>
              </div>
              <div className="flex gap-6 text-sm">
                <div className="text-center">
                  <p className="text-teal-400 font-bold">$55</p>
                  <p className="text-xs text-slate-500">Ship/pc</p>
                </div>
                <div className="text-center">
                  <p className="text-teal-400 font-bold">$35</p>
                  <p className="text-xs text-slate-500">Vest Ship</p>
                </div>
                <div className="text-center">
                  <p className="text-amber-400 font-bold">4.5%</p>
                  <p className="text-xs text-slate-500">Bank</p>
                </div>
                <div className="text-center">
                  <p className="text-blue-400 font-bold">{pricing.markup}×</p>
                  <p className="text-xs text-slate-500">Markup</p>
                </div>
              </div>
            </div>
          </div>

          {/* RC CMT Labor Pricing Reference */}
          <div className="mb-6">
            <button
              onClick={() => setShowMxCmtRef(!showMxCmtRef)}
              className="w-full bg-[#111c2e] rounded-xl p-4 border border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="rc-cmt-toggle"
            >
              <div className="flex items-center gap-3">
                <Scissors size={18} className="text-blue-400" />
                <div className="text-left">
                  <p className="font-semibold text-white">RC CMT Labor Costs</p>
                  <p className="text-xs text-slate-400">Construction labor costs by garment type</p>
                </div>
              </div>
              {showMxCmtRef ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>

            {showMxCmtRef && (
              <div className="mt-2">
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1a2a40]">
                          <th className="py-3 px-4 text-left text-slate-400">Workmanship</th>
                          <th className="py-3 px-3 text-center text-slate-400">2pc Suit</th>
                          <th className="py-3 px-3 text-center text-slate-400">3pc Suit</th>
                          <th className="py-3 px-3 text-center text-slate-400">Jacket</th>
                          <th className="py-3 px-3 text-center text-slate-400">Pants</th>
                          <th className="py-3 px-3 text-center text-slate-400">Vest</th>
                          <th className="py-3 px-3 text-center text-slate-400">Shirt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Fused", prices: [160, 210, 110, 50, 50, 30] },
                          { label: "Half Canvas", prices: [180, 230, 130, 50, 50, 30], highlight: true },
                          { label: "Full Canvas", prices: [260, 310, 210, 50, 50, 30] },
                          { label: "Unstructured Canvas", prices: [180, 230, 135, 50, 50, 30] },
                        ].map((row, idx) => (
                          <tr key={idx} className={`border-b border-white/5 ${row.highlight ? "bg-blue-500/10" : "hover:bg-white/5"}`}>
                            <td className="py-3 px-4 font-medium">{row.label}</td>
                            {row.prices.map((p, i) => (
                              <td key={i} className="py-3 px-3 text-center font-mono text-emerald-400">${p}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTD Process & CMT Reference */}
          <div className="mb-6">
            <button
              onClick={() => setShowCtdProcessRef(!showCtdProcessRef)}
              className="w-full bg-[#111c2e] rounded-xl p-4 border border-teal-500/20 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="ctd-process-toggle"
            >
              <div className="flex items-center gap-3">
                <Scissors size={18} className="text-teal-400" />
                <div className="text-left">
                  <p className="font-semibold text-white">CTD Process & CMT Reference</p>
                  <p className="text-xs text-slate-400">Top Line & High-End Line — Handmade vs Machine breakdown</p>
                </div>
              </div>
              {showCtdProcessRef ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>

            {showCtdProcessRef && (
              <div className="mt-2 space-y-4">
                {/* Jacket Process */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="bg-[#1a2a40] px-4 py-2 flex items-center justify-between">
                    <p className="text-sm font-semibold text-white">Jacket Process</p>
                    <div className="flex gap-4 text-[10px]">
                      <span className="text-amber-400">TOP LINE</span>
                      <span className="text-purple-400">HIGH-END LINE</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#0f1a2e]">
                          <th className="py-2 px-3 text-left text-slate-400">Process</th>
                          <th className="py-2 px-2 text-center text-amber-400">Full Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-amber-400">1/2 Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 Handmade<br/>Half Canvas</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine<br/>Half Canvas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["Canvas sewing", "H", "M", "M", "M", "M", "M"],
                          ["Lapel pad-stitching", "H", "M", "M", "M", "M", "M"],
                          ["Collar pad-stitching", "M", "M", "M", "M", "M", "M"],
                          ["Gorge line sewing", "H", "H", "M", "M", "M", "M"],
                          ["Inner shoulder sewing", "H", "H", "M", "M", "M", "M"],
                          ["Collar felt sewing", "H", "H", "H", "H", "H", "H"],
                          ["Sleeve lining sewing", "H", "H", "½H", "½H", "½H", "½H"],
                          ["Wadding cotton sewing", "H", "H", "M", "M", "M", "M"],
                          ["Sleeves sewing", "M", "M", "M", "M", "M", "M"],
                          ["Sleeves slit", "H", "H", "M", "M", "M", "M"],
                          ["Cuffs pad-stitching", "M", "M", "F", "F", "F", "F"],
                          ["Jacket bottom sewing", "H", "H", "M", "M", "M", "M"],
                          ["Inner facing", "H", "M", "M", "M", "M", "M"],
                          ["Pick-stitching", "H", "H", "M", "M", "M", "M"],
                          ["Canvas pad-stitching", "M", "M", "M", "M", "M", "M"],
                          ["Shoulder pad sewing", "H", "H", "M", "M", "M", "M"],
                          ["Breast pocket", "H", "H", "H", "H", "H", "H"],
                          ["Label stitching", "H", "H", "H", "M", "H", "M"],
                          ["Bar tacks", "H", "H", "H", "M", "H", "M"],
                          ["Button holes", "H", "H", "H", "M", "H", "M"],
                          ["Buttons sewing", "H", "H", "H", "H", "H", "H"],
                          ["Lower pockets cutting", "H", "H", "H", "M", "H", "M"],
                          ["Inner pockets cutting", "H", "H", "M", "M", "M", "M"],
                          ["Inseam overlocking", "H", "M", "M", "M", "M", "M"],
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-1.5 px-3 text-slate-300">{row[0]}</td>
                            {row.slice(1).map((cell, i) => (
                              <td key={i} className={`py-1.5 px-2 text-center font-medium ${
                                cell === "H" ? "text-emerald-400" : cell === "½H" ? "text-yellow-400" : cell === "F" ? "text-orange-400" : "text-slate-500"
                              }`}>
                                {cell === "H" ? "Handmade" : cell === "½H" ? "Half HM" : cell === "F" ? "Fusing" : "Machine"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="bg-teal-500/10 border-t border-teal-500/30">
                          <td className="py-2 px-3 font-bold text-teal-400">CMT / USD</td>
                          {[430, 260, 222, 175, 182, 135].map((p, i) => (
                            <td key={i} className="py-2 px-2 text-center font-bold text-teal-400">${p}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pants Process */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="bg-[#1a2a40] px-4 py-2">
                    <p className="text-sm font-semibold text-white">Pants Process</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#0f1a2e]">
                          <th className="py-2 px-3 text-left text-slate-400">Process</th>
                          <th className="py-2 px-2 text-center text-amber-400">Full HM FC</th>
                          <th className="py-2 px-2 text-center text-amber-400">1/2 HM FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 HM FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 HM HC</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine HC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["Pick-stitching", "H", "H", "M", "M", "M", "M"],
                          ["Inner waistband", "H", "M", "M", "M", "M", "M"],
                          ["Back pockets", "H", "H", "M", "M", "M", "M"],
                          ["Pant cuff overlocking", "H", "H", "M", "M", "M", "M"],
                          ["Button holes", "H", "H", "H", "M", "H", "M"],
                          ["Buttons sewing", "H", "H", "H", "H", "H", "H"],
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-1.5 px-3 text-slate-300">{row[0]}</td>
                            {row.slice(1).map((cell, i) => (
                              <td key={i} className={`py-1.5 px-2 text-center font-medium ${cell === "H" ? "text-emerald-400" : "text-slate-500"}`}>
                                {cell === "H" ? "Handmade" : "Machine"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="bg-teal-500/10 border-t border-teal-500/30">
                          <td className="py-2 px-3 font-bold text-teal-400">CMT / USD</td>
                          {[145, 130, 60, 44, 57, 41].map((p, i) => (
                            <td key={i} className="py-2 px-2 text-center font-bold text-teal-400">${p}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Vest Process */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="bg-[#1a2a40] px-4 py-2">
                    <p className="text-sm font-semibold text-white">Vest Process</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#0f1a2e]">
                          <th className="py-2 px-3 text-left text-slate-400">Process</th>
                          <th className="py-2 px-2 text-center text-amber-400">Full HM FC</th>
                          <th className="py-2 px-2 text-center text-amber-400">1/2 HM FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 HM FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine FC</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 HM HC</th>
                          <th className="py-2 px-2 text-center text-purple-400">Machine HC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["Lower pockets cutting", "H", "H", "H", "M", "H", "M"],
                          ["Bar tacks", "H", "H", "H", "M", "H", "M"],
                          ["Button holes", "H", "H", "H", "M", "H", "M"],
                          ["Buttons sewing", "H", "H", "H", "H", "H", "H"],
                          ["Pick-stitching", "H", "H", "M", "M", "M", "M"],
                          ["Canvas pad-stitching", "H", "H", "H", "H", "F", "F"],
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-1.5 px-3 text-slate-300">{row[0]}</td>
                            {row.slice(1).map((cell, i) => (
                              <td key={i} className={`py-1.5 px-2 text-center font-medium ${
                                cell === "H" ? "text-emerald-400" : cell === "F" ? "text-orange-400" : "text-slate-500"
                              }`}>
                                {cell === "H" ? "Handmade" : cell === "F" ? "Fusing" : "Machine"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="bg-teal-500/10 border-t border-teal-500/30">
                          <td className="py-2 px-3 font-bold text-teal-400">CMT / USD</td>
                          {[130, 130, 60, 44, 57, 41].map((p, i) => (
                            <td key={i} className="py-2 px-2 text-center font-bold text-teal-400">${p}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>


          {/* CTD Production Time Reference */}
          <div className="mb-6">
            <button
              onClick={() => setShowCtdProductionTime(!showCtdProductionTime)}
              className="w-full bg-[#111c2e] rounded-xl p-4 border border-orange-500/20 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="ctd-production-toggle"
            >
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-orange-400" />
                <div className="text-left">
                  <p className="font-semibold text-white">CTD Production Time</p>
                  <p className="text-xs text-slate-400">Normal & Super Rush service lead times</p>
                </div>
              </div>
              {showCtdProductionTime ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>

            {showCtdProductionTime && (
              <div className="mt-2 bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                <div className="p-4 space-y-4">
                  {/* Normal Service */}
                  <div>
                    <h4 className="text-sm font-bold text-emerald-400 mb-2">Normal Service</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        { label: "Regular Line", days: "9 working days" },
                        { label: "High-End Line", days: "11 working days", note: "Single Pants: 9 days" },
                        { label: "XY Line", days: "12 working days" },
                        { label: "Shirt Line", days: "2 weeks", important: true },
                        { label: "Half Handmade", days: "15 working days" },
                        { label: "Full Handmade", days: "18 working days" },
                        { label: "Casual Jackets Line", days: "19 working days" },
                      ].map((item, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-2 rounded ${item.important ? "bg-red-500/10 border border-red-500/30" : "bg-[#0a1628]"}`}>
                          <span className={`text-xs ${item.important ? "text-red-400 font-bold" : "text-slate-300"}`}>{item.label}</span>
                          <div className="text-right">
                            <span className={`text-xs font-mono ${item.important ? "text-red-400 font-bold" : "text-emerald-400"}`}>{item.days}</span>
                            {item.note && <p className="text-[10px] text-slate-500">{item.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-amber-400 mt-2">* Order with style picture: +3 days</p>
                  </div>

                  {/* Super Rush Service */}
                  <div className="border-t border-white/10 pt-3">
                    <h4 className="text-sm font-bold text-amber-400 mb-2">Super Rush Service</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="flex items-center justify-between p-2 rounded bg-[#0a1628]">
                        <span className="text-xs text-slate-300">Regular Line</span>
                        <span className="text-xs font-mono text-amber-400">5 suits/day max</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-[#0a1628]">
                        <span className="text-xs text-slate-300">High-End Line</span>
                        <span className="text-xs font-mono text-amber-400">Available</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-red-500/10 border border-red-500/30">
                        <span className="text-xs text-red-400 font-bold">Shirt Line</span>
                        <span className="text-xs font-mono text-red-400 font-bold">No Service</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Price Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            {[
              { type: "2piece", label: "2 Piece Suit", icon: "🤵" },
              { type: "3piece", label: "3 Piece Suit", icon: "👔" },
              { type: "jacket", label: "Jacket", icon: "🧥" },
              { type: "pant", label: "Pants", icon: "👖" },
              { type: "vest", label: "Vest", icon: "🦺" },
              { type: "shirt", label: "Shirt", icon: "👕" },
            ].map(item => {
              const suitPrices = (FABRIC_DATA.suits || []).map(f => f.price).filter(p => typeof p === 'number');
              const minPrice = suitPrices.length ? calculatePrice(Math.min(...suitPrices), item.type) : 0;
              const maxPrice = suitPrices.length ? calculatePrice(Math.max(...suitPrices), item.type) : 0;
              return (
                <div key={item.type} className={`rounded-xl p-4 border ${isBigTall ? "bg-amber-500/10 border-amber-500/30" : "bg-[#111c2e] border-white/10"}`}>
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <p className="text-sm text-slate-400">{item.label}</p>
                  <p className="text-lg font-bold">${minPrice.toLocaleString()} - ${maxPrice.toLocaleString()}</p>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search by code, color, pattern..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-[#0a1628] border-white/10" />
                </div>
              </div>
              <select value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                {books.map(book => (<option key={book} value={book}>{book === "all" ? "All Books" : book}</option>))}
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="code">Code A-Z</option>
                <option value="color">Color A-Z</option>
              </select>
            </div>
            <p className="text-sm text-slate-400 mt-3">Showing {filteredFabrics.length} fabrics</p>
          </div>

          {/* Fabric List */}
          <div className="space-y-3">
            {filteredFabrics.map((fabric, index) => {
              const tier = getFabricTier(fabric.price);
              const isExpanded = expandedFabric === fabric.rc_code;
              return (
                <div key={`${fabric.rc_code}-${index}`} className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <button className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors" onClick={() => setExpandedFabric(isExpanded ? null : fabric.rc_code)}>
                    <div className="w-12 h-12 rounded-lg border border-white/20 flex-shrink-0" style={{ backgroundColor: fabric.color.toLowerCase().includes('navy') ? '#1e3a5f' : fabric.color.toLowerCase().includes('black') ? '#1a1a1a' : '#4a5568' }} />
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold">{fabric.rc_code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${tier.bg} ${tier.color}`}>{tier.name}</span>
                      </div>
                      <p className="text-sm text-slate-400">{fabric.color} • {fabric.pattern} • {fabric.composition}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Fabric/meter</p>
                      <p className="text-lg font-bold text-emerald-400">${fabric.price}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-white/10 pt-4">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="text-xs text-slate-400">
                          {pricing.constructionType} • {pricing.markup}× Markup
                          {isBigTall && <span className="text-amber-400 ml-2">+15% Big & Tall</span>}
                        </p>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-teal-400">+$55 Ship ($35 Vest)</span>
                          <span className="text-amber-400">+4.5% Bank</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        {[{ type: "2piece", label: "2 Piece" }, { type: "3piece", label: "3 Piece" }, { type: "jacket", label: "Jacket" }, { type: "pant", label: "Pants" }, { type: "vest", label: "Vest" }, { type: "shirt", label: "Shirt" }].map(item => {
                          const isCtdSelected = pricing.constructionType.startsWith("CTD ");
                          const fabricCost = Math.round(fabric.price * pricing.fabricUsage[item.type]);
                          const laborCost = pricing.laborCosts[item.type] || 0;
                          const shipping = isCtdSelected ? (CTD_SHIPPING[item.type] || 55) : (RC_SHIPPING[item.type] || 55);
                          const baseCost = fabricCost + laborCost + shipping;
                          const finalPrice = calculatePrice(fabric.price, item.type);
                          return (
                            <div key={item.type} className={`bg-[#0a1628] rounded-lg p-3 text-center border ${isCtdSelected ? "border-teal-500/20" : "border-blue-500/20"}`}>
                              <p className="text-xs text-slate-500">{item.label}</p>
                              <p className="text-xs text-slate-600">Fabric: ${fabricCost}</p>
                              <p className="text-xs text-blue-400/70">CMT: ${laborCost}</p>
                              {shipping > 0 && <p className="text-xs text-amber-500/80">Ship: ${shipping}</p>}
                              <p className="text-xl font-bold text-emerald-400">${finalPrice.toLocaleString()}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MX Fabrics (Retail) Content - With Search, Filters & Fabric List */}
      {mainTab === "mx-retail" && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Quick Settings Bar */}
          <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Construction:</span>
                <div className="flex gap-1 flex-wrap">
                  {ALL_CONSTRUCTION_TYPES.map(type => (
                    <button
                      key={type}
                      onClick={() => setMxConstructionType(type)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        mxConstructionType === type ? "bg-purple-500 text-white" : "bg-[#0a1628] text-slate-400 hover:text-white"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Markup:</span>
                <div className="flex gap-1">
                  {[2.0, 2.5, 3.0, 3.5, 4.0].map(mult => (
                    <button
                      key={mult}
                      onClick={() => setMxMarkup(mult)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        mxMarkup === mult ? "bg-purple-500 text-white" : "bg-[#0a1628] text-slate-400 hover:text-white"
                      }`}
                    >
                      {mult}×
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* MX Retail Fee Structure Info */}
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/30 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-purple-400 mb-1">MX Retail Pricing (Landed + Ship + {mxMarkup}× Markup + 4.5% Bank)</h3>
                <p className="text-sm text-slate-400">Landed Cost (Fabric + Labor) + Shipping → <span className="text-purple-400">Final Retail Price</span></p>
                {isCTD && <p className="text-xs text-teal-400 mt-1">CTD: $55/piece shipping ($35 vest) — Suit = $110</p>}
                {!isCTD && <p className="text-xs text-amber-400 mt-1">MX: $55/suit shipping ($35 vest)</p>}
              </div>
              <div className="flex gap-6 text-sm">
                <div className="text-center">
                  <p className="text-purple-400 font-bold">{mxMarkup}×</p>
                  <p className="text-xs text-slate-500">Markup</p>
                </div>
                <div className="text-center">
                  <p className="text-amber-400 font-bold">{isCTD ? "$55/pc" : "$55/suit"}</p>
                  <p className="text-xs text-slate-500">Shipping</p>
                </div>
                <div className="text-center">
                  <p className="text-amber-400 font-bold">$35</p>
                  <p className="text-xs text-slate-500">Vest Ship</p>
                </div>
                <div className="text-center">
                  <p className="text-amber-400 font-bold">4.5%</p>
                  <p className="text-xs text-slate-500">Bank</p>
                </div>
              </div>
            </div>
          </div>

          {/* MX CMT & Premium Pricing Reference */}
          <div className="mb-6">
            <button
              onClick={() => setShowMxCmtRef(!showMxCmtRef)}
              className="w-full bg-[#111c2e] rounded-xl p-4 border border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="mx-cmt-toggle"
            >
              <div className="flex items-center gap-3">
                <Scissors size={18} className="text-purple-400" />
                <div className="text-left">
                  <p className="font-semibold text-white">MX CMT Labor & Premium Line Pricing</p>
                  <p className="text-xs text-slate-400">CMT costs, Top Line, High-End Line, Production Time & Fitting</p>
                </div>
              </div>
              {showMxCmtRef ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
            </button>

            {showMxCmtRef && (
              <div className="mt-2 space-y-4">
                {/* CMT Labor Costs */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h4 className="font-semibold text-blue-400 flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-blue-500/20 text-xs">LABOR ONLY</span>
                      CMT (Cut, Make, Trim) — No Fabric Included
                    </h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1a2a40]">
                          <th className="py-3 px-4 text-left text-slate-400">Workmanship</th>
                          <th className="py-3 px-3 text-center text-slate-400">2pc Suit</th>
                          <th className="py-3 px-3 text-center text-slate-400">Jacket</th>
                          <th className="py-3 px-3 text-center text-slate-400">Pants</th>
                          <th className="py-3 px-3 text-center text-slate-400">Vest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Fused", prices: [115, 75, 40, 40] },
                          { label: "Standard Half Canvas", prices: [145, 105, 40, 40] },
                          { label: "High-End Half Canvas", prices: [176, 135, 41, 41], highlight: true },
                          { label: "Full Canvas", prices: [219, 175, 44, 44] },
                        ].map((row, idx) => (
                          <tr key={idx} className={`border-b border-white/5 ${row.highlight ? "bg-purple-500/10" : "hover:bg-white/5"}`}>
                            <td className="py-3 px-4 font-medium">{row.label}</td>
                            {row.prices.map((p, i) => (
                              <td key={i} className="py-3 px-3 text-center font-mono text-emerald-400">${p}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top Line & High-End Line */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h4 className="font-semibold text-amber-400">Top Line & High-End Line CMT Pricing</h4>
                    <p className="text-xs text-slate-400 mt-1">Premium workmanship tiers with detailed construction methods</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1a2a40]">
                          <th className="py-3 px-4 text-left text-slate-400" rowSpan={2}>Garment</th>
                          <th className="py-2 px-2 text-center text-amber-400 border-b border-amber-500/30" colSpan={3}>TOP LINE</th>
                          <th className="py-2 px-2 text-center text-purple-400 border-b border-purple-500/30" colSpan={3}>HIGH-END LINE</th>
                        </tr>
                        <tr className="bg-[#1a2a40]">
                          <th className="py-2 px-2 text-center text-xs text-slate-400">Full Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-xs text-slate-400">1/2 Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-xs text-slate-400">1/3 Handmade<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-xs text-slate-400">Machine<br/>Full Canvas</th>
                          <th className="py-2 px-2 text-center text-xs text-slate-400">1/3 Handmade<br/>Half Canvas</th>
                          <th className="py-2 px-2 text-center text-xs text-slate-400">Machine<br/>Half Canvas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Jacket", prices: [430, 260, 222, 175, 182, 135] },
                          { label: "Pants", prices: [145, 130, 60, 44, 57, 41] },
                          { label: "Vest", prices: [130, 130, 60, 44, 57, 41] },
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 font-medium">{row.label}</td>
                            {row.prices.map((p, i) => (
                              <td key={i} className={`py-3 px-2 text-center font-mono ${i < 3 ? "text-amber-400" : "text-purple-400"}`}>${p}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Production Time & Fitting Costs side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Production Time */}
                  <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h4 className="font-semibold text-sky-400">Production Time (Workdays)</h4>
                      <p className="text-xs text-slate-400 mt-1">After technician approval, excl. Sun & holidays</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#1a2a40]">
                            <th className="py-2 px-3 text-left text-xs text-slate-400">Workmanship</th>
                            <th className="py-2 px-3 text-center text-xs text-slate-400">Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: "Full Handmade Full Canvas", days: 20, tier: "top" },
                            { label: "1/2 Handmade Full Canvas", days: 15, tier: "top" },
                            { label: "1/3 Handmade Full Canvas", days: 13, tier: "top" },
                            { label: "Machine Full Canvas", days: 10, tier: "high" },
                            { label: "1/3 Handmade Half Canvas", days: 13, tier: "high" },
                            { label: "Machine Half Canvas", days: 10, tier: "high" },
                          ].map((row, idx) => (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-2 px-3 text-xs">{row.label}</td>
                              <td className={`py-2 px-3 text-center font-mono font-bold ${row.tier === "top" ? "text-amber-400" : "text-purple-400"}`}>{row.days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Fitting Costs */}
                  <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h4 className="font-semibold text-rose-400">Fitting Costs (USD)</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#1a2a40]">
                            <th className="py-2 px-3 text-left text-xs text-slate-400" rowSpan={2}>Garment</th>
                            <th className="py-1 px-2 text-center text-xs text-amber-400 border-b border-amber-500/30" colSpan={3}>TOP LINE</th>
                            <th className="py-1 px-2 text-center text-xs text-purple-400 border-b border-purple-500/30" colSpan={3}>HIGH-END LINE</th>
                          </tr>
                          <tr className="bg-[#1a2a40]">
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">HE Baste</th>
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">Baste</th>
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">Muslin</th>
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">HE Baste</th>
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">Baste</th>
                            <th className="py-1 px-2 text-center text-[10px] text-slate-400">Muslin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: "Jacket", prices: [90, 40, 50, 56, 25, 45] },
                            { label: "Pants", prices: [40, 15, 25, 24, 10, 19] },
                            { label: "Vest", prices: [40, 15, 25, 24, 10, 1] },
                          ].map((row, idx) => (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-2 px-3 text-xs font-medium">{row.label}</td>
                              {row.prices.map((p, i) => (
                                <td key={i} className={`py-2 px-2 text-center font-mono text-xs ${i < 3 ? "text-amber-400" : "text-purple-400"}`}>${p}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Detailed Workmanship Comparison */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h4 className="font-semibold text-slate-300">Jacket Construction Detail</h4>
                    <p className="text-xs text-slate-400 mt-1">Handmade vs Machine comparison by step</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#1a2a40]">
                          <th className="py-2 px-3 text-left text-slate-400">Step</th>
                          <th className="py-2 px-2 text-center text-amber-400">Full HM<br/>Full C</th>
                          <th className="py-2 px-2 text-center text-amber-400">1/2 HM<br/>Full C</th>
                          <th className="py-2 px-2 text-center text-amber-400">1/3 HM<br/>Full C</th>
                          <th className="py-2 px-2 text-center text-purple-400">Mach<br/>Full C</th>
                          <th className="py-2 px-2 text-center text-purple-400">1/3 HM<br/>Half C</th>
                          <th className="py-2 px-2 text-center text-purple-400">Mach<br/>Half C</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["Canvas sewing", "H", "M", "M", "M", "M", "M"],
                          ["Lapel pad-stitching", "H", "M", "M", "M", "M", "M"],
                          ["Gorge line sewing", "H", "H", "M", "M", "M", "M"],
                          ["Inner shoulder sewing", "H", "H", "M", "M", "M", "M"],
                          ["Collar felt sewing", "H", "H", "H", "H", "H", "H"],
                          ["Sleeve lining sewing", "H", "H", "1/2H", "1/2H", "1/2H", "1/2H"],
                          ["Wadding cotton sewing", "H", "H", "M", "M", "M", "M"],
                          ["Sleeves slit", "H", "H", "M", "M", "M", "M"],
                          ["Cuffs pad-stitching", "M", "M", "F", "F", "F", "F"],
                          ["Jacket bottom sewing", "H", "H", "M", "M", "M", "M"],
                          ["Inner facing", "H", "M", "M", "M", "M", "M"],
                          ["Pick-stitching", "H", "H", "M", "M", "M", "M"],
                          ["Shoulder pad sewing", "H", "H", "M", "M", "M", "M"],
                          ["Breast pocket", "H", "H", "H", "H", "H", "H"],
                          ["Label stitching", "H", "H", "H", "M", "H", "M"],
                          ["Bar tacks", "H", "H", "H", "M", "H", "M"],
                          ["Button holes", "H", "H", "H", "M", "H", "M"],
                          ["Buttons sewing", "H", "H", "H", "H", "H", "H"],
                          ["Lower pockets cutting", "H", "H", "H", "M", "H", "M"],
                          ["Inner pockets cutting", "H", "H", "M", "M", "M", "M"],
                          ["Inseam overlocking", "H", "M", "M", "M", "M", "M"],
                        ].map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-1.5 px-3 text-slate-300">{row[0]}</td>
                            {row.slice(1).map((cell, i) => (
                              <td key={i} className={`py-1.5 px-2 text-center font-medium ${
                                cell === "H" ? "text-emerald-400" : cell === "1/2H" ? "text-sky-400" : cell === "F" ? "text-amber-400" : "text-slate-500"
                              }`}>
                                {cell === "H" ? "Hand" : cell === "M" ? "Mach" : cell === "1/2H" ? "Half" : cell === "F" ? "Fuse" : cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search and Filters */}
          <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search by code, color, pattern..." value={mxSearchQuery} onChange={(e) => setMxSearchQuery(e.target.value)} className="pl-10 bg-[#0a1628] border-white/10" />
                </div>
              </div>
              <select value={mxSelectedBook} onChange={(e) => setMxSelectedBook(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                {mxBooks.map(book => (<option key={book} value={book}>{book === "all" ? "All Books" : book}</option>))}
              </select>
              <select value={mxSortBy} onChange={(e) => setMxSortBy(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="code">Code A-Z</option>
                <option value="color">Color A-Z</option>
              </select>
            </div>
            <p className="text-sm text-slate-400 mt-3">Showing {filteredMXFabrics.length} fabrics</p>
          </div>

          {/* Fabric List with Retail Pricing */}
          <div className="space-y-3">
            {filteredMXFabrics.map((fabric, index) => {
              const tier = getFabricTier(fabric.price);
              const isExpanded = mxExpandedFabric === `mx-retail-${fabric.rc_code}-${index}`;
              const pricingCategory = getMXPricingCategory(fabric.rc_code);
              const categoryData = MX_SUITS_DATA.find(d => d.category === pricingCategory);
              const workmanship = WORKMANSHIP_KEY_MAP[mxConstructionType] || "halfCanvas";

              return (
                <div key={`mx-retail-${fabric.rc_code}-${index}`} className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  <button className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors" onClick={() => setMxExpandedFabric(isExpanded ? null : `mx-retail-${fabric.rc_code}-${index}`)}>
                    <div className="w-12 h-12 rounded-lg border border-white/20 flex-shrink-0" style={{ backgroundColor: fabric.color.toLowerCase().includes('navy') ? '#1e3a5f' : fabric.color.toLowerCase().includes('black') ? '#1a1a1a' : fabric.color.toLowerCase().includes('grey') || fabric.color.toLowerCase().includes('gray') ? '#6b7280' : fabric.color.toLowerCase().includes('brown') ? '#8b5a2b' : fabric.color.toLowerCase().includes('blue') ? '#3b82f6' : '#4a5568' }} />
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-purple-400">{fabric.rc_code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${tier.bg} ${tier.color}`}>{tier.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">{fabric.book}</span>
                      </div>
                      <p className="text-sm text-slate-400">{fabric.color} • {fabric.pattern} • {fabric.composition}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Fabric/meter</p>
                      <p className="text-lg font-bold text-purple-400">${fabric.price}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-white/10 pt-4">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="text-xs text-slate-400">
                          {mxConstructionType} • <span className="text-purple-400">{mxMarkup}× Retail Markup</span>
                          {pricingCategory && <span className="ml-2 text-blue-400">• Category: {pricingCategory}</span>}
                        </p>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-amber-400">{isCTD ? "+$55/pc Ship ($35 Vest)" : "+$55/suit Ship ($35 Vest)"}</span>
                          <span className="text-amber-400">+4.5% Bank</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {categoryData && categoryData.fused ? (
                          // Use category-based landed pricing (computed dynamically for premium tiers)
                          <>
                            {[
                              { key: "suit2pc", label: "2 Piece Suit" },
                              { key: "jacket", label: "Jacket" },
                              { key: "pants", label: "Pants" },
                              { key: "vest", label: "Vest" },
                              { key: "overcoat", label: "Overcoat" },
                            ].map(item => {
                              const landedPrice = getLandedPrice(categoryData, workmanship, item.key);
                              const landed = typeof landedPrice === 'string' ? landedPrice.split('/')[0] : landedPrice;
                              const shipping = getShipping(item.key);
                              const retailPrice = landed ? Math.round((parseFloat(landed) + shipping) * 1.045 * mxMarkup) : null;
                              return (
                                <div key={item.key} className="bg-[#0a1628] rounded-lg p-3 text-center border border-purple-500/20">
                                  <p className="text-xs text-slate-500">{item.label}</p>
                                  <p className="text-xs text-slate-600">Landed: {landed ? `$${landed}` : "–"}</p>
                                  <p className="text-xs text-amber-500/80">Ship: ${shipping}</p>
                                  <p className="text-xl font-bold text-purple-400">{retailPrice ? `$${retailPrice.toLocaleString()}` : "–"}</p>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          // Fallback message
                          <div className="col-span-5 text-center text-slate-500 text-sm py-4">
                            No landed pricing available for this fabric category
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edit Pricing Modal */}
      {editingPricing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingPricing(false)}>
          <div className="bg-[#111c2e] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#111c2e]">
              <h3 className="text-lg font-bold">Edit Pricing Structure</h3>
              <button onClick={() => setEditingPricing(false)} className="text-slate-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Calculator size={18} className="text-blue-400" /> Labor Costs</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(tempPricing.laborCosts).map(([key, value]) => (
                    <div key={key} className="bg-[#0a1628] rounded-lg p-3">
                      <label className="text-xs text-slate-400 capitalize">{key.replace("piece", " Piece")}</label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-slate-500">$</span>
                        <Input type="number" value={value} onChange={(e) => setTempPricing(prev => ({ ...prev, laborCosts: { ...prev.laborCosts, [key]: Number(e.target.value) } }))} className="bg-transparent border-white/10 text-lg font-bold" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Package size={18} className="text-purple-400" /> Fabric Usage (meters)</h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(tempPricing.fabricUsage).map(([key, value]) => (
                    <div key={key} className="bg-[#0a1628] rounded-lg p-3">
                      <label className="text-xs text-slate-400 capitalize">{key.replace("piece", " Piece")}</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="number" step="0.1" value={value} onChange={(e) => setTempPricing(prev => ({ ...prev, fabricUsage: { ...prev.fabricUsage, [key]: Number(e.target.value) } }))} className="bg-transparent border-white/10 text-lg font-bold" />
                        <span className="text-slate-500">m</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex gap-3">
              <Button variant="outline" className="flex-1 border-white/20" onClick={() => setEditingPricing(false)}>Cancel</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={savePricing}><Save size={16} className="mr-2" /> Save Changes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPriceListPage;
